// src/pages/SocietesList.js - VERSION AVEC CHARTE GRAPHIQUE SECURECONTROL
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const SocietesList = () => {
  const navigate = useNavigate();
  
  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentProfile, setCurrentProfile] = useState(null);
  const [currentSocieteId, setCurrentSocieteId] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatut, setFilterStatut] = useState("");

  // Palette de couleurs SecureControl
  const COLORS = {
    primary: '#0B3C5D',     // Bleu Sécurité
    secondary: '#1E88C8',   // Bleu Tech
    accent: '#4FC3F7',      // Bleu Clair
    darkText: '#2E2E2E',    // Gris Foncé
    lightBg: '#F5F7FA',     // Gris Clair
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  // Récupérer les infos de l'utilisateur connecté
  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setCurrentProfile(String(userData.profile));
        setCurrentSocieteId(Number(userData.societe_id));
      } catch (e) {
        console.error("Erreur parse user:", e);
      }
    }
  }, []);

  // Fonction pour charger la liste des sociétés
  const fetchSocietes = useCallback(async () => {
    setLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/societes/list.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        let filteredSocietes = response.data.societes || [];
        
        // Appliquer les filtres
        if (search) {
          const searchLower = search.toLowerCase();
          filteredSocietes = filteredSocietes.filter(societe =>
            societe.nom_societe.toLowerCase().includes(searchLower) ||
            societe.email_societe.toLowerCase().includes(searchLower) ||
            societe.nif_societe.toLowerCase().includes(searchLower) ||
            societe.rccm_societe.toLowerCase().includes(searchLower)
          );
        }
        
        if (filterStatut) {
          const statutValue = parseInt(filterStatut);
          filteredSocietes = filteredSocietes.filter(societe =>
            societe.statut_societe === statutValue
          );
        }
        
        setSocietes(filteredSocietes);
      } else {
        setError(response.data.message || "Erreur de chargement.");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setError("Accès interdit. Permissions insuffisantes.");
      } else if (!err.response) {
        setError("Serveur indisponible.");
      } else {
        setError(`Erreur ${err.response.status}: ${err.response.statusText}`);
      }
    } finally {
      setLoading(false);
    }
  }, [search, filterStatut, navigate]);

  useEffect(() => {
    fetchSocietes();
  }, [fetchSocietes]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSocietes();
  };

  const clearFilters = () => {
    setSearch("");
    setFilterStatut("");
    fetchSocietes();
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 1:
        return (
          <span className="badge fw-semibold" style={{ 
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '0.8rem'
          }}>
            <i className="bi bi-check-circle me-1"></i>
            Actif
          </span>
        );
      case 0:
        return (
          <span className="badge fw-semibold" style={{ 
            backgroundColor: '#f5f5f5',
            color: '#757575',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '0.8rem'
          }}>
            <i className="bi bi-x-circle me-1"></i>
            Inactif
          </span>
        );
      default:
        return (
          <span className="badge fw-semibold" style={{ 
            backgroundColor: '#fff8e1',
            color: '#ff8f00',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '0.8rem'
          }}>
            <i className="bi bi-question-circle me-1"></i>
            Inconnu
          </span>
        );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  // Fonction pour formater les valeurs null/vides
  const formatValue = (value, defaultValue = '-') => {
    if (!value || value.trim() === '') return defaultValue;
    return value;
  };

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des sociétés</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Sociétés</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            {["1", "3", "2"].includes(currentProfile) ? (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="card-title mb-1" style={{ 
                      color: COLORS.primary,
                      borderBottom: `2px solid ${COLORS.lightBg}`,
                      paddingBottom: '16px',
                      width: '100%'
                    }}>
                      <div className="d-flex align-items-center">
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                          width: '36px',
                          height: '36px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-buildings"></i>
                        </div>
                        Liste des sociétés
                      </div>
                    </h5>
                    
                  </div>
                </div>

                {/* Filtres */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card" style={{ 
                      backgroundColor: '#E3F2FD',
                      border: 'none',
                      borderRadius: '8px'
                    }}>
                      <div className="card-body py-3">
                        <form onSubmit={handleSearch} className="row g-3">
                          <div className="col-md-5">
                            <div className="input-group">
                              <span className="input-group-text" style={{ 
                                backgroundColor: COLORS.lightBg,
                                borderColor: '#e0e0e0',
                                color: COLORS.secondary
                              }}>
                                <i className="bi bi-search"></i>
                              </span>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Rechercher par nom, email, NIF ou RCCM..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                style={{
                                  borderColor: '#e0e0e0',
                                  color: COLORS.darkText
                                }}
                              />
                            </div>
                          </div>
                          
                          <div className="col-md-3">
                            <div className="input-group">
                              <span className="input-group-text" style={{ 
                                backgroundColor: COLORS.lightBg,
                                borderColor: '#e0e0e0',
                                color: COLORS.secondary
                              }}>
                                <i className="bi bi-funnel"></i>
                              </span>
                              <select
                                className="form-select"
                                value={filterStatut}
                                onChange={(e) => setFilterStatut(e.target.value)}
                                style={{
                                  borderColor: '#e0e0e0',
                                  color: COLORS.darkText
                                }}
                              >
                                <option value="">Tous les statuts</option>
                                <option value="1">Actif</option>
                                <option value="0">Inactif</option>
                              </select>
                            </div>
                          </div>
                          
                          <div className="col-md-4">
                            <div className="d-flex gap-2">
                              <button 
                                type="submit" 
                                className="btn fw-semibold"
                                style={{
                                  backgroundColor: COLORS.primary,
                                  borderColor: COLORS.primary,
                                  color: COLORS.white,
                                  borderRadius: '8px',
                                  padding: '10px 20px',
                                  flex: 1,
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                <i className="bi bi-search me-2"></i> 
                                Rechercher
                              </button>
                              
                              {(search || filterStatut) && (
                                <button
                                  type="button"
                                  className="btn fw-semibold"
                                  onClick={clearFilters}
                                  style={{
                                    color: COLORS.primary,
                                    borderColor: COLORS.primary,
                                    backgroundColor: 'transparent',
                                    borderRadius: '8px',
                                    padding: '10px 16px',
                                    transition: 'all 0.3s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#E3F2FD';
                                    e.target.style.borderColor = COLORS.secondary;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.borderColor = COLORS.primary;
                                  }}
                                >
                                  <i className="bi bi-x-lg me-1"></i>
                                  Effacer
                                </button>
                              )}
                            </div>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="alert alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: COLORS.danger,
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.danger}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">{error}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setError("")}
                      ></button>
                    </div>
                    <div className="mt-3">
                      <button 
                        className="btn btn-sm fw-semibold"
                        onClick={fetchSocietes}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '6px',
                          padding: '6px 16px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = COLORS.primary;
                          e.target.style.color = COLORS.white;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = COLORS.primary;
                        }}
                      >
                        <i className="bi bi-arrow-clockwise me-1"></i> 
                        Réessayer
                      </button>
                    </div>
                  </div>
                )}

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des sociétés...</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderBottom: `2px solid ${COLORS.accent}`
                          }}>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Société</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Contact</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Identifiants</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Adresse</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Statut</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Créée le</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', width: '80px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {societes.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="text-center py-5">
                                <div className="mb-4">
                                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                                    width: '80px',
                                    height: '80px',
                                    backgroundColor: '#F5F7FA',
                                    color: '#B0BEC5'
                                  }}>
                                    <i className="bi bi-buildings fs-1"></i>
                                  </div>
                                </div>
                                <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucune société trouvée</h6>
                                <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                                  {search || filterStatut 
                                    ? "Aucune société ne correspond à vos critères de recherche." 
                                    : "Aucune société n'est actuellement enregistrée dans le système."}
                                </p>
                                {(search || filterStatut) && (
                                  <button
                                    className="btn fw-semibold mt-3"
                                    onClick={clearFilters}
                                    style={{
                                      color: COLORS.primary,
                                      borderColor: COLORS.primary,
                                      backgroundColor: 'transparent',
                                      borderRadius: '8px',
                                      padding: '8px 20px',
                                      transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#E3F2FD';
                                      e.target.style.borderColor = COLORS.secondary;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'transparent';
                                      e.target.style.borderColor = COLORS.primary;
                                    }}
                                  >
                                    <i className="bi bi-x-lg me-2"></i>
                                    Effacer les filtres
                                  </button>
                                )}
                              </td>
                            </tr>
                          ) : (
                            societes.map((societe) => (
                              <tr 
                                key={societe.id_societe} 
                                style={{ 
                                  borderBottom: `1px solid ${COLORS.lightBg}`
                                }}
                              >
                                <td style={{ padding: '16px' }}>
                                  <div className="d-flex align-items-center">
                                    <div className="me-3">
                                      <div 
                                        className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                                        style={{ 
                                          width: '44px', 
                                          height: '44px', 
                                          fontSize: '15px',
                                          backgroundColor: COLORS.primary,
                                          boxShadow: '0 2px 4px rgba(11, 60, 93, 0.1)'
                                        }}
                                      >
                                        <i className="bi bi-building"></i>
                                      </div>
                                    </div>
                                    <div>
                                      <div style={{ 
                                        color: COLORS.primary, 
                                        fontWeight: '500',
                                        fontSize: '0.95rem'
                                      }}>
                                        {societe.nom_societe}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <div>
                                    <div className="d-flex align-items-center mb-2">
                                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                        width: '20px',
                                        height: '20px',
                                        backgroundColor: '#E3F2FD',
                                        color: COLORS.secondary,
                                        fontSize: '0.7rem'
                                      }}>
                                        <i className="bi bi-envelope"></i>
                                      </div>
                                      <small style={{ color: COLORS.darkText }}>{formatValue(societe.email_societe)}</small>
                                    </div>
                                    <div className="d-flex align-items-center">
                                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                        width: '20px',
                                        height: '20px',
                                        backgroundColor: '#E3F2FD',
                                        color: COLORS.secondary,
                                        fontSize: '0.7rem'
                                      }}>
                                        <i className="bi bi-telephone"></i>
                                      </div>
                                      <small style={{ color: COLORS.darkText }}>{formatValue(societe.tel_societe)}</small>
                                    </div>
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <div className="d-flex flex-column gap-2">
                                    <div>
                                      <div className="small" style={{ color: '#6c757d', marginBottom: '4px' }}>NIF</div>
                                      <div className="d-flex align-items-center">
                                        <span className="badge fw-semibold" style={{ 
                                          backgroundColor: COLORS.lightBg,
                                          color: formatValue(societe.nif_societe) === '-' ? '#9e9e9e' : COLORS.darkText,
                                          border: `1px solid #e0e0e0`,
                                          padding: '4px 8px',
                                          borderRadius: '4px',
                                          fontSize: '0.75rem',
                                          fontStyle: formatValue(societe.nif_societe) === '-' ? 'italic' : 'normal'
                                        }}>
                                          {formatValue(societe.nif_societe)}
                                        </span>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="small" style={{ color: '#6c757d', marginBottom: '4px' }}>RCCM</div>
                                      <div className="d-flex align-items-center">
                                        <span className="badge fw-semibold" style={{ 
                                          backgroundColor: COLORS.lightBg,
                                          color: formatValue(societe.rccm_societe) === '-' ? '#9e9e9e' : COLORS.darkText,
                                          border: `1px solid #e0e0e0`,
                                          padding: '4px 8px',
                                          borderRadius: '4px',
                                          fontSize: '0.75rem',
                                          fontStyle: formatValue(societe.rccm_societe) === '-' ? 'italic' : 'normal'
                                        }}>
                                          {formatValue(societe.rccm_societe)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <div>
                                    <div className="mb-2">
                                      <small style={{ 
                                        color: formatValue(societe.address_societe) === '-' ? '#9e9e9e' : COLORS.darkText,
                                        fontSize: '0.85rem',
                                        lineHeight: 1.4,
                                        fontStyle: formatValue(societe.address_societe) === '-' ? 'italic' : 'normal'
                                      }}>
                                        {formatValue(societe.address_societe)}
                                      </small>
                                    </div>
                                    {societe.code_postal_societe && societe.code_postal_societe.trim() !== '' && (
                                      <div className="d-flex align-items-center">
                                        <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                          width: '20px',
                                          height: '20px',
                                          backgroundColor: '#E3F2FD',
                                          color: COLORS.secondary,
                                          fontSize: '0.7rem'
                                        }}>
                                          <i className="bi bi-mailbox"></i>
                                        </div>
                                        <small style={{ 
                                          color: COLORS.darkText,
                                          fontSize: '0.8rem'
                                        }}>
                                          {societe.code_postal_societe}
                                        </small>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  {getStatutBadge(societe.statut_societe)}
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <div className="d-flex align-items-center">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                      width: '20px',
                                      height: '20px',
                                      backgroundColor: '#E3F2FD',
                                      color: COLORS.secondary,
                                      fontSize: '0.7rem'
                                    }}>
                                      <i className="bi bi-calendar"></i>
                                    </div>
                                    <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                                      {formatDate(societe.created_at)}
                                    </small>
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <div className="position-relative">
                                    <button
                                      className="btn fw-semibold"
                                      onClick={() => navigate(`/societes/details/${societe.id_societe}`)}
                                      title="Voir les détails de la société"
                                      style={{
                                        color: COLORS.primary,
                                        borderColor: COLORS.primary,
                                        backgroundColor: 'transparent',
                                        borderRadius: '6px',
                                        padding: '8px',
                                        width: '36px',
                                        height: '36px',
                                        transition: 'all 0.3s ease'
                                      }}
                                    >
                                      <i className="bi bi-eye"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Informations sur le nombre de sociétés */}
                    <div className="mt-4 pt-3 border-top" style={{ borderColor: COLORS.lightBg }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <small className="text-muted" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                            <i className="bi bi-info-circle me-1" style={{ color: COLORS.primary }}></i>
                            <strong style={{ color: COLORS.primary }}>{societes.length}</strong> société{societes.length !== 1 ? 's' : ''} trouvée{societes.length !== 1 ? 's' : ''}
                          </small>
                        </div>
                        
                        {currentProfile === "1" && societes.length > 0 && (
                          <div className="d-flex gap-3">
                            <small className="text-muted" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                              <span className="badge fw-semibold" style={{ 
                                backgroundColor: '#e8f5e9',
                                color: '#2e7d32',
                                padding: '4px 12px',
                                borderRadius: '6px'
                              }}>
                                <i className="bi bi-check-circle me-1"></i>
                                {societes.filter(s => s.statut_societe === 1).length} active{societes.filter(s => s.statut_societe === 1).length !== 1 ? 's' : ''}
                              </span>
                            </small>
                            
                            <small className="text-muted" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                              <span className="badge fw-semibold" style={{ 
                                backgroundColor: '#f5f5f5',
                                color: '#757575',
                                padding: '4px 12px',
                                borderRadius: '6px'
                              }}>
                                <i className="bi bi-x-circle me-1"></i>
                                {societes.filter(s => s.statut_societe === 0).length} inactive{societes.filter(s => s.statut_societe === 0).length !== 1 ? 's' : ''}
                              </span>
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              // Affichage pour les agents non autorisés
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#F5F7FA',
                    color: '#B0BEC5'
                  }}>
                    <i className="bi bi-lock fs-1"></i>
                  </div>
                </div>
                <h5 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Accès restreint</h5>
                <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                  Vous n'avez pas les permissions nécessaires pour accéder à cette page. Cette fonctionnalité est réservée aux administrateurs et gestionnaires.
                </p>
                
                <button
                  className="btn fw-semibold mt-4"
                  onClick={() => navigate("/dashboard")}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = COLORS.white;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour au tableau de bord
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .btn:focus,
        .form-control:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          transition: background-color 0.2s ease;
        }
        
        .badge {
          font-weight: 500;
        }
        
        /* Tooltip custom */
        [title] {
          position: relative;
        }
        
        [title]:hover::after {
          content: attr(title);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background-color: ${COLORS.primary};
          color: white;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          white-space: nowrap;
          z-index: 1000;
          margin-bottom: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        [title]:hover::before {
          content: '';
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          border-width: 5px;
          border-style: solid;
          border-color: ${COLORS.primary} transparent transparent transparent;
          margin-bottom: 3px;
          z-index: 1000;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .table-responsive {
            border: none;
          }
          
          .table {
            font-size: 0.875rem;
          }
          
          .table th,
          .table td {
            padding: 12px 8px !important;
          }
          
          .col-md-5,
          .col-md-3,
          .col-md-4 {
            margin-bottom: 12px;
          }
          
          .d-flex.gap-2 {
            justify-content: center;
          }
        }
        
        /* Amélioration de la lisibilité des tables */
        .table th {
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.85rem;
        }
        
        .table td {
          vertical-align: top;
        }
        
        /* Animation pour les badges */
        .badge:hover {
          transform: translateY(-1px);
        }
        
        /* Uniformisation de la hauteur des cellules */
        .table td {
          min-height: 80px;
        }
        
        /* Style pour les valeurs manquantes */
        .text-muted-italic {
          font-style: italic;
          color: #9e9e9e !important;
        }
      `}</style>
    </Layout>
  );
};

export default SocietesList;